// src/components/ReportsComboSubscriptionPage.js
import React, { useState } from 'react';
import Layout from './Layout';
import { useNavigate } from 'react-router-dom';
import { auth } from '../firebaseConfig';
import { setDoc } from 'firebase/firestore';
import { useUserContext } from '../UserContext';
import { resolveSubscriptionDoc } from '../subscriptionDoc';

// You can override this in .env as REACT_APP_COMBO_ONETIME_AMOUNT_PAISE
// For testing: set it to "500" (₹5). For real: "149900" (₹1499).
const ONE_TIME_AMOUNT_PAISE = parseInt(
  process.env.REACT_APP_COMBO_ONETIME_AMOUNT_PAISE || '149900',
  10
);

const ReportsComboSubscriptionPage = () => {
  const navigate = useNavigate();
  const { refreshUserDetails } = useUserContext();

  const [errorMessage, setErrorMessage] = useState('');
  // separate busy flags per button
  const [busySubscription, setBusySubscription] = useState(false);
  const [busyOneTime, setBusyOneTime] = useState(false);
  const [busyCancel, setBusyCancel] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // ---------- common helpers ----------
  const getUserAndDeviceInfo = () => {
    const u = auth.currentUser;
    const name = u?.displayName || 'Tender Managers User';
    const email = u?.email || '';
    const contact = (u?.phoneNumber || '').replace(/[^\d+]/g, '');

    const ua = navigator.userAgent || '';
    const isIOS = /iP(hone|ad|od)/.test(ua);
    const isSafari = /^((?!chrome|android).)*safari/i.test(ua);
    const iOSSafari = isIOS && isSafari;

    return { name, email, contact, iOSSafari };
  };

  /**
   * Extend tracker validity AND set the reports monthly quota.
   *
   * Tracker:
   *   newTrackerEnd = max(existing end, today) + 30 days
   *
   * Reports combo:
   *   cycle = exactly 30 days from *today* (date of this combo purchase)
   *   i.e. cycleStart = today, cycleEnd = today + 30 days
   *
   * extra (optional):
   *   { razorpaySubscriptionId?: string | null }
   */
  const extendTrackerAndQuota = async (currentUser, extra = {}) => {
    const phoneNumber = currentUser.phoneNumber;
    const { ref: docRef, snap: docSnap } = await resolveSubscriptionDoc(phoneNumber);

    const today = new Date();
    const PLAN_DAYS = 30;
    const planDurationMs = PLAN_DAYS * 24 * 60 * 60 * 1000;

    // ----- 1) Tracker extension logic -----
    let trackerBaseDate = today;

    if (docSnap.exists()) {
      const data = docSnap.data();
      if (data.endDate) {
        const raw = data.endDate;
        const existingEnd = raw?.toDate
          ? raw.toDate()
          : raw instanceof Date
          ? raw
          : new Date(raw);

        if (!isNaN(existingEnd?.getTime()) && existingEnd.getTime() > today.getTime()) {
          trackerBaseDate = existingEnd;
        }
      }
    }

    const newTrackerEnd = new Date(trackerBaseDate.getTime() + planDurationMs);

    // ----- 2) Reports combo monthly cycle (ALWAYS 30 days from today) -----
    const reportsCycleStart = today;
    const reportsCycleEnd = new Date(today.getTime() + planDurationMs);

    const dataToSave = {
      plan: 'comboTR',          // Tracker + Reports combo
      startDate: today,         // last purchase date for tracker
      endDate: newTrackerEnd,   // tracker valid till
      status: 'Active',
      reportsCombo: {
        planType: 'monthly_1499',
        monthlyRowQuota: 5000,
        usedRows: 0,
        cycleStart: reportsCycleStart,
        cycleEnd: reportsCycleEnd,
      },
    };

    // If we have a Razorpay subscription id (auto-renew combo)
    if (extra.razorpaySubscriptionId) {
      dataToSave.reportsComboSubscription = {
        razorpaySubscriptionId: extra.razorpaySubscriptionId,
        autoRenew: true,
        lastUpdated: today,
      };
    }

    await setDoc(docRef, dataToSave, { merge: true });

    try {
      await refreshUserDetails();
    } catch (err) {
      console.error('Error refreshing user context after combo update:', err);
    }

    navigate('/tender-selection');
  };

  // ---------- 1) MONTHLY SUBSCRIPTION flow ----------
  const handleSubscribe = async () => {
    const currentUser = auth.currentUser;

    if (!currentUser) {
      setErrorMessage('Please login to start the subscription.');
      setSuccessMessage('');
      navigate('/login');
      return;
    }

    setErrorMessage('');
    setSuccessMessage('');
    setBusySubscription(true);

    const { name, email, contact, iOSSafari } = getUserAndDeviceInfo();

    try {
      const resp = await fetch('/api/create-combo-subscription', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, contact }),
      });

      const subData = await resp.json();

      if (!resp.ok || !subData.subscriptionId || !subData.key_id) {
        console.error('Subscription creation failed:', subData);
        setErrorMessage('Could not start subscription. Please try again.');
        setBusySubscription(false);
        return;
      }

      const options = {
        key: subData.key_id,
        subscription_id: subData.subscriptionId,
        name: 'Tender Managers',
        description: 'Tracker + Reports Combo (Monthly)',
        prefill: { name, email, contact },
        ...(iOSSafari ? { config: { upi: { flow: 'collect' } } } : {}),
        handler: async function (response) {
          try {
            const {
              razorpay_payment_id,
              razorpay_subscription_id,
              razorpay_signature,
            } = response;

            const verifyResp = await fetch('/api/verify-combo-subscription', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                razorpay_payment_id,
                razorpay_subscription_id,
                razorpay_signature,
              }),
            });

            const verifyData = await verifyResp.json();
            if (verifyData.status === 'success') {
              // Save tracker+reports info AND Razorpay subscription id for auto-renew
              await extendTrackerAndQuota(currentUser, {
                razorpaySubscriptionId: razorpay_subscription_id,
              });
            } else {
              setErrorMessage('Subscription verification failed. Please contact support.');
            }
          } catch (err) {
            console.error('Error in subscription handler:', err);
            setErrorMessage('Error completing subscription.');
          } finally {
            setBusySubscription(false);
          }
        },
        modal: {
          ondismiss: () => {
            setErrorMessage('Subscription window closed.');
            setBusySubscription(false);
          },
        },
        theme: { color: '#F37254' },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (error) {
      console.error('Error starting combo subscription:', error);
      setErrorMessage('Failed to start subscription. Please try again.');
      setBusySubscription(false);
    }
  };

  // ---------- 2) ONE-TIME PAYMENT flow ----------
  const handleOneTimePayment = async () => {
    const currentUser = auth.currentUser;

    if (!currentUser) {
      setErrorMessage('Please login to make the payment.');
      setSuccessMessage('');
      navigate('/login');
      return;
    }

    setErrorMessage('');
    setSuccessMessage('');
    setBusyOneTime(true);

    const { name, email, contact, iOSSafari } = getUserAndDeviceInfo();

    try {
      const orderResp = await fetch('/api/create-order', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount: ONE_TIME_AMOUNT_PAISE }),
      });

      const orderData = await orderResp.json();
      if (!orderResp.ok || !orderData.id) {
        console.error('Order creation failed:', orderData);
        setErrorMessage('Could not start payment. Please try again.');
        setBusyOneTime(false);
        return;
      }

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: orderData.amount,
        currency: orderData.currency,
        name: 'Tender Managers',
        description: 'Tracker + Reports Combo (1 month, one-time payment)',
        order_id: orderData.id,
        prefill: { name, email, contact },
        ...(iOSSafari ? { config: { upi: { flow: 'collect' } } } : {}),
        handler: async function (response) {
          try {
            const {
              razorpay_order_id,
              razorpay_payment_id,
              razorpay_signature,
            } = response;

            const verifyResp = await fetch('/api/verify-payment', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                razorpay_order_id,
                razorpay_payment_id,
                razorpay_signature,
              }),
            });

            const verifyData = await verifyResp.json();

            if (verifyData.status === 'success') {
              // One-time payment: no subscription id, so just extend tracker+quota
              await extendTrackerAndQuota(currentUser);
            } else {
              setErrorMessage(
                'Payment verification failed. Please try again or contact support.'
              );
            }
          } catch (err) {
            console.error('Error in one-time payment handler:', err);
            setErrorMessage(
              'An error occurred after payment. Please contact support.'
            );
          } finally {
            setBusyOneTime(false);
          }
        },
        modal: {
          ondismiss: () => {
            setErrorMessage('Payment window closed.');
            setBusyOneTime(false);
          },
        },
        theme: { color: '#F37254' },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (error) {
      console.error('Error starting one-time payment:', error);
      setErrorMessage('Failed to start payment. Please try again.');
      setBusyOneTime(false);
    }
  };

  // ---------- 3) CANCEL AUTO-RENEWAL (stop future charges only) ----------
  const handleCancelAutoRenewal = async () => {
    const currentUser = auth.currentUser;

    if (!currentUser) {
      setErrorMessage('Please login to manage your subscription.');
      setSuccessMessage('');
      navigate('/login');
      return;
    }

    setErrorMessage('');
    setSuccessMessage('');
    setBusyCancel(true);

    try {
      const phoneNumber = currentUser.phoneNumber;
      const { ref: docRef, snap } = await resolveSubscriptionDoc(phoneNumber);

      if (!snap.exists()) {
        setErrorMessage('No active subscription record found.');
        setBusyCancel(false);
        return;
      }

      const data = snap.data();
      const subMeta = data.reportsComboSubscription;

      if (!subMeta || !subMeta.razorpaySubscriptionId) {
        setErrorMessage('No active auto-renewing combo subscription found.');
        setBusyCancel(false);
        return;
      }

      const subscriptionId = subMeta.razorpaySubscriptionId;

      const resp = await fetch('/api/cancel-combo-subscription', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ subscriptionId }),
      });

      const respData = await resp.json();

      if (!resp.ok || respData.status !== 'success') {
        console.error('Failed to cancel subscription:', respData);
        setErrorMessage(
          respData.message || 'Unable to cancel subscription. Please contact support.'
        );
        setBusyCancel(false);
        return;
      }

      // Mark auto-renew as false in Firestore
      const updatedMeta = {
        ...subMeta,
        autoRenew: false,
        cancelledAt: new Date(),
      };

      await setDoc(
        docRef,
        { reportsComboSubscription: updatedMeta },
        { merge: true }
      );

      try {
        await refreshUserDetails();
      } catch (e) {
        console.error('Error refreshing user after cancel:', e);
      }

      setSuccessMessage(
        'Auto-renewal cancelled. Your current paid period will remain active; no future charges will be made.'
      );
    } catch (err) {
      console.error('Error cancelling auto-renewal:', err);
      setErrorMessage('Something went wrong while cancelling. Please try again or contact support.');
    } finally {
      setBusyCancel(false);
    }
  };

  return (
    <Layout>
      <div style={{ maxWidth: 600, margin: '24px auto', padding: 16 }}>
        <h1>Tracker + Reports Combo</h1>
        <p>
          This plan gives you full <strong>Tender Tracker</strong> access plus a
          monthly quota of <strong>up to 5,000 report rows</strong> from our GeM
          procurement reports.
        </p>

        <p>
          You can either use <strong>auto-renew subscription</strong> or make{' '}
          <strong>one-time payment</strong> and manually renew next month.
        </p>

        <div
          style={{
            display: 'flex',
            gap: 12,
            marginTop: 20,
            flexWrap: 'wrap',
          }}
        >
          <button
            onClick={handleSubscribe}
            disabled={busySubscription}
            style={{
              padding: '10px 18px',
              borderRadius: 8,
              border: 'none',
              background: '#16a34a',
              color: '#fff',
              cursor: busySubscription ? 'not-allowed' : 'pointer',
              flex: '1 1 220px',
            }}
          >
            {busySubscription ? 'Please wait…' : 'Start Monthly Subscription'}
          </button>

          <button
            onClick={handleOneTimePayment}
            disabled={busyOneTime}
            style={{
              padding: '10px 18px',
              borderRadius: 8,
              border: '1px solid #16a34a',
              background: '#ecfdf3',
              color: '#166534',
              cursor: busyOneTime ? 'not-allowed' : 'pointer',
              flex: '1 1 220px',
            }}
          >
            {busyOneTime ? 'Please wait…' : 'Pay Once for 1 Month'}
          </button>
        </div>

        <div
          style={{
            marginTop: 24,
            paddingTop: 16,
            borderTop: '1px solid #e5e7eb',
          }}
        >
          <h3 style={{ marginBottom: 8 }}>Manage Auto-Renewal</h3>
          <p style={{ marginBottom: 8, fontSize: 14, color: '#4b5563' }}>
            If you are on the monthly auto-renewing combo plan and want to stop future
            charges while keeping the current paid period active, use the button below.
          </p>
          <button
            onClick={handleCancelAutoRenewal}
            disabled={busyCancel}
            style={{
              padding: '8px 16px',
              borderRadius: 8,
              border: '1px solid #b91c1c',
              background: '#fef2f2',
              color: '#b91c1c',
              cursor: busyCancel ? 'not-allowed' : 'pointer',
            }}
          >
            {busyCancel ? 'Cancelling…' : 'Cancel Auto-Renewal Subscription'}
          </button>
        </div>

        {errorMessage && (
          <p style={{ marginTop: 12, color: 'red' }}>{errorMessage}</p>
        )}
        {successMessage && (
          <p style={{ marginTop: 12, color: 'green' }}>{successMessage}</p>
        )}
      </div>
    </Layout>
  );
};

export default ReportsComboSubscriptionPage;
